import { GQL } from "@binale-tech/shared";

import { AUTOMATION_JOB_POLL_MS, isJobFinished } from "@domain/graphql/automationJob";

import { withThinking } from "../runner/runAgent";
import type { AgentContext, AgentIO, AgentStep, ReportLine } from "../runner/types";
import { planDraftJobs, type DraftJob } from "./draftSelection";

const MAX_POLL_ERRORS = 3;
export const JOB_STALL_MS = 600_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type JobEnd =
    | { kind: "finished"; job: GQL.IAutomationJob }
    | { kind: "cancelled" | "stalled"; job: GQL.IAutomationJob | null };

/** Polls one job until it ends, the run is closed or its progress stalls. */
const pollJob = async (ctx: AgentContext, io: AgentIO, jobId: string, progressId: string, total: number) => {
    let errors = 0;
    let last: GQL.IAutomationJob | null = null;
    let lastDone = -1;
    let lastChange = Date.now();
    for (;;) {
        try {
            last = await ctx.readAutomationJob(jobId);
            errors = 0;
        } catch (error) {
            errors += 1;
            if (errors >= MAX_POLL_ERRORS) throw error;
        }
        if (last) {
            const done = last.succeeded + last.skipped + last.failed;
            io.update(progressId, { progress: { done: done, total } });
            if (done !== lastDone) {
                lastDone = done;
                lastChange = Date.now();
            }
            if (isJobFinished(last)) return { kind: "finished", job: last } satisfies JobEnd;
        }
        if (io.isCancelled()) return { kind: "cancelled", job: last } satisfies JobEnd;
        if (Date.now() - lastChange >= JOB_STALL_MS) return { kind: "stalled", job: last } satisfies JobEnd;
        await sleep(AUTOMATION_JOB_POLL_MS);
    }
};

const reasonLine = (reason: GQL.IDmsDocumentToRecordReason, count: number): ReportLine => ({
    textId: "app.agent.report.draftRefused",
    values: { count },
    labels: { reason: `app.automation.d2r.reason.${reason}` },
});

export const draftsFromDocumentsStep: AgentStep = {
    id: "draftsFromDocuments",
    async run(ctx, io) {
        if (!ctx.canRead(GQL.IProductKey.Dms)) {
            io.narrateSkipped(GQL.IProductKey.Dms);
            return { status: "skipped" };
        }
        const targets = [GQL.IProductKey.Er, GQL.IProductKey.Bank, GQL.IProductKey.Kb];
        if (!targets.some((p) => ctx.canWrite(p))) {
            for (const p of targets) io.narrateSkipped(p);
            return { status: "skipped" };
        }
        const found = await withThinking(io, "app.agent.drafts.thinking", () => ctx.findDraftDocuments());
        const { jobs, withoutGroup } = planDraftJobs(found, ctx.documentGroupId);
        if (jobs.length === 0 && withoutGroup === 0) return { status: "nothing" };

        const refused = new Map<GQL.IDmsDocumentToRecordReason, number>();
        const addRefused = (reason: GQL.IDmsDocumentToRecordReason, n = 1) =>
            refused.set(reason, (refused.get(reason) ?? 0) + n);
        if (withoutGroup > 0) addRefused(GQL.IDmsDocumentToRecordReason.MissingGroup, withoutGroup);

        const open: ReportLine[] = [];
        let created = 0;
        let failed = 0;
        let running = 0;
        let continuesOnServer = false;

        const runJob = async (job: DraftJob): Promise<boolean> => {
            const total = job.documentIds.length;
            const labels = { product: `app.titles.${job.productKey}` } as const;
            const progressId = io.post({
                kind: "progress",
                textId: "app.agent.drafts.progress",
                labels,
                progress: { done: 0, total },
            });
            const jobId = await ctx.enqueueDocumentsToRecords(job.productKey, job.groupId, job.documentIds);
            const end = await pollJob(ctx, io, jobId, progressId, total);
            if (end.kind !== "finished") {
                running += total;
                continuesOnServer = true;
                if (end.kind === "stalled") io.post({ kind: "text", textId: "app.agent.continuesOnServer" });
                return false;
            }
            const results = end.job.documentsToRecordsResults ?? [];
            let made = 0;
            for (const r of results) {
                if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Created) {
                    made += 1;
                    ctx.created.recordIds.push(...r.recordIds);
                } else if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Refused && r.reason) {
                    addRefused(r.reason);
                } else if (r.outcome === GQL.IDmsDocumentToRecordOutcome.Failed) {
                    if (r.reason) addRefused(r.reason);
                    else failed += 1;
                }
            }
            if (results.length === 0 && end.job.status === GQL.IAutomationJobStatus.Failed) failed += total;
            created += made;
            if (made > 0) io.post({ kind: "done", textId: "app.agent.drafts.done", values: { count: made }, labels });
            return true;
        };

        for (const job of jobs) {
            if (io.isCancelled()) break;
            if (!ctx.canWrite(job.productKey)) {
                io.narrateSkipped(job.productKey);
                continue;
            }
            if (!(await runJob(job))) break;
        }

        for (const [reason, count] of refused) open.push(reasonLine(reason, count));
        if (failed > 0) open.push({ textId: "app.agent.report.draftsFailed", values: { count: failed } });
        if (running > 0) open.push({ textId: "app.agent.report.draftsRunning", values: { count: running } });

        const summary: ReportLine[] =
            created > 0 ? [{ textId: "app.agent.report.draftsCreated", values: { count: created } }] : [];
        const status = created > 0 || open.length > 0 || continuesOnServer ? "done" : "nothing";
        return { status, summary, open, ...(continuesOnServer ? { data: { continuesOnServer: true } } : {}) };
    },
};
